// How much of the screen the on-screen keyboard is covering.
//
// Two signals, whichever reports more:
//
//   * `visualViewport` — the browser's own account of what is covered. This is
//     the only thing that works on iOS Safari, which ignores the
//     `interactive-widget` viewport meta entirely.
//   * Telegram's `viewportChanged` — inside a Mini App the page lives in a
//     webview whose usable height Telegram reports itself. `viewportHeight` is
//     what you can see right now, `viewportStableHeight` is what you get with
//     transient UI gone, so the gap between them is the keyboard.
//
// Taking the larger of the two means neither has to be complete on its own:
// whichever platform answers, answers.

import { useEffect, useState } from 'react'

interface TgViewport {
  viewportHeight?: number
  viewportStableHeight?: number
  onEvent?: (event: string, cb: () => void) => void
  offEvent?: (event: string, cb: () => void) => void
}

function telegram(): TgViewport | null {
  const w = window as unknown as { Telegram?: { WebApp?: TgViewport } }
  return w.Telegram?.WebApp ?? null
}

/**
 * Anything smaller than this is not a keyboard.
 *
 * Mobile Safari collapses its address bar as you scroll, which moves the
 * visual viewport by a few dozen pixels. Reacting to that would make the
 * composer twitch during ordinary scrolling.
 */
const MIN_KEYBOARD = 80

export function useKeyboardInset(): number {
  const [inset, setInset] = useState(0)

  useEffect(() => {
    const vv = window.visualViewport

    const measure = () => {
      let px = 0

      if (vv) {
        // offsetTop matters: iOS scrolls the visual viewport within the layout
        // viewport rather than resizing it, so height alone under-reports.
        px = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
      }

      const tg = telegram()
      if (tg?.viewportHeight && tg.viewportStableHeight) {
        px = Math.max(px, tg.viewportStableHeight - tg.viewportHeight)
      }

      setInset(px > MIN_KEYBOARD ? Math.round(px) : 0)
    }

    measure()
    vv?.addEventListener('resize', measure)
    vv?.addEventListener('scroll', measure)
    const tg = telegram()
    tg?.onEvent?.('viewportChanged', measure)

    return () => {
      vv?.removeEventListener('resize', measure)
      vv?.removeEventListener('scroll', measure)
      tg?.offEvent?.('viewportChanged', measure)
    }
  }, [])

  return inset
}
